<template>
  <teleport to="body">
    <!-- backdrop semitransparente -->
    <div v-if="visible" class="modal-backdrop" @click="closeModal" aria-hidden="true"></div>

    <div v-if="visible" class="modal-content" role="dialog" aria-modal="true" aria-label="Logs de usuarios">
       <div class="modal-header">
         <h3 class="modal-title">{{ title || $t('logs_todos_usuarios') }}</h3>
         <button type="button" class="btn-close" @click.stop="closeModal" aria-label="Cerrar" title="Cerrar">
           <span class="material-icons">close</span>
         </button>
       </div>
       <div class="modal-body">
         <div class="filter-container">
           <label for="filter-date">{{ $t('filtrar_fecha') }}</label>
           <input
             type="date"
             id="filter-date"
             v-model="filterDate"
             @change="filterLogsByDate"
           />
            <button type="button" class="logout-button" @click="clearDateFilter">{{ $t('borrar_filtro') }}</button>
            <button type="button" class="logout-button btn-export" @click="exportToExcel">{{ $t('exportar_logs') }}</button>
           </div>
         <div class="logs-container">
           <table class="logs-table">
             <thead>
               <tr>
                 <th>{{ $t('id') }}</th>
                 <th>{{ $t('usuario') }}</th>
                 <th>{{ $t('inicio_sesion') }}</th>
                 <th>{{ $t('fin_sesion') }}</th>
                 <th>{{ $t('expulsado') }}</th>
                 <th>{{ $t('motivo_fallo') }}</th>
               </tr>
             </thead>
             <tbody>
               <tr v-if="filteredLogs.length === 0">
                 <td colspan="6" style="text-align: center;">{{ $t('no_registros') }}</td>
               </tr>
               <tr
                 v-for="(log, index) in filteredLogs"
                 :key="index"
                 :class="{ 'login-fallido': log.es_fallido === 1 }"
               >
                 <td>{{ log.id }}</td>
                 <td>{{ log.nombre_usuario || $t('desconocido') }}</td>
                 <td>{{ log.inicio_sesion }}</td>
                 <td>
                   <template v-if="log.es_fallido === 1"></template>
                   <template v-else>{{ log.fin_sesion || $t('en_sesion') }}</template>
                 </td>
                 <td class="expulsado-column">
                   <span v-if="log.expulsado === 1" class="expulsado-check">✔️</span>
                 </td>
                 <td>
                   <span :class="{ 'fallo': log.es_fallido === 1 }">{{ log.motivo_fallo || '' }}</span>
                 </td>
               </tr>
             </tbody>
           </table>
         </div>
       </div>
       <div class="modal-footer">
         <button type="button" class="close-btn" @click="closeModal">{{ $t('cerrar') }}</button>
       </div>
     </div>

   </teleport>
 </template>

<script>
import * as XLSX from 'xlsx';

export default {
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    logs: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      default: 'Logs de Todos los Usuarios',
    },
  },
  emits: ['close'],
  methods: {
    exportToExcel() {
      const header = [
        this.$t('id'),
        this.$t('usuario'),
        this.$t('inicio_sesion'),
        this.$t('fin_sesion'),
        this.$t('expulsado'),
        this.$t('motivo_fallo')
      ];
      const data = this.filteredLogs.map(log => [
        log.id,
        log.nombre_usuario || this.$t('desconocido'),
        log.inicio_sesion,
        log.es_fallido === 1 ? '' : (log.fin_sesion || this.$t('en_sesion')),
        log.expulsado === 1 ? '✔️' : '',
        log.motivo_fallo || ''
      ]);
      const allRows = [header, ...data];
      const ws = XLSX.utils.aoa_to_sheet(allRows);

      // Calcular el ancho máximo de cada columna
      const colWidths = header.map((_, colIdx) => {
        return {
          wch: Math.max(
            ...allRows.map(row => (row[colIdx] ? String(row[colIdx]).length : 0)),
            10 // ancho mínimo
          )
        };
      });
      ws['!cols'] = colWidths;

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Logs");
      const timestamp = new Date().toISOString().slice(0,19).replace(/[:T]/g,'-');
      XLSX.writeFile(wb, `logs_usuarios_${timestamp}.xlsx`);
    },
    closeModal() {
      this.$emit('close');
    },
    filterLogsByDate() {
      if (this.filterDate) {
        this.filteredLogs = this.logs.filter((log) =>
          typeof log.inicio_sesion === 'string' && log.inicio_sesion.startsWith(this.filterDate)
        );
      } else {
        this.filteredLogs = [...this.logs]; // copiar array para no mutar la prop
      }
    },
    clearDateFilter() {
      this.filterDate = '';
      this.filteredLogs = [...this.logs];
    }
  },
  data() {
    return {
      filterDate: '', // Fecha seleccionada para el filtro
      filteredLogs: [], // Logs filtrados (se inicializa desde el watcher)
    };
  },
  watch: {
    logs: {
      immediate: true,
      handler(newLogs) {
        this.filteredLogs = Array.isArray(newLogs) ? [...newLogs] : [];
      },
    },
    // Quitar/poner scroll del body cuando el modal se muestra/oculta
    visible(newVal) {
      if (typeof document === 'undefined') return;
      if (newVal) {
        // guardar valor previo por si hay otros modales
        this._previousBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
      } else {
        // restaurar
        document.body.style.overflow = this._previousBodyOverflow || '';
        this._previousBodyOverflow = null;
      }
    }
  },
  beforeUnmount() {
    // asegurar restauración si el componente se destruye con modal abierto
    if (typeof document !== 'undefined') {
      document.body.style.overflow = this._previousBodyOverflow || '';
      this._previousBodyOverflow = null;
    }
  },
};
</script>

<style scoped>
/* ocultar scrollbar visual del modal content */
.modal-content {
  overflow: hidden !important;        /* no mostrar barras en el contenedor principal */
  -ms-overflow-style: none;           /* IE/Edge */
  scrollbar-width: none;              /* Firefox */
}
.modal-content::-webkit-scrollbar {   /* Chrome, Safari, Opera */
  display: none;
}

.modal-content {
  position: fixed !important;
  top: 55% !important; /* movido un poco hacia abajo */
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  z-index: 12000 !important;
  background-color: #ffffff !important;
  border-radius: 8px;
  width: 49%;
  max-width: 95vw;
  max-height: 70vh;
  overflow: visible;
  box-shadow: 0 10px 30px rgba(0,0,0,0.25);
  box-sizing: border-box;
  padding: 0;
  display: flex;
  flex-direction: column;
}

/* header y footer fijos (no participan en el reparto de altura) */
.modal-header,
.modal-footer {
  flex: 0 0 auto;
}

/* body ocupa el espacio restante */
.modal-body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  overflow: hidden; /* el scroll se delega a .logs-container */
  box-sizing: border-box;
}

/* logs-container rellena el body y hace el scroll interno */
.logs-container {
  flex: 1 1 auto;
  overflow-y: auto;
  max-height: none;     /* anula max-height previos que provocaban overflow excesivo */
  padding-bottom: 80px; /* espacio para que el último elemento no quede tapado por el footer */
  box-sizing: border-box;
}

/* header ocupa su espacio (fijo) */
.modal-header {
  padding: 10px 16px;
  box-sizing: border-box;
  margin-top: 0;
  left: 0;
}

.modal-header .btn-close {
  /* mismo color que el botón cerrar del footer (rojo corporativo) */
  background-color: #99172A !important;
  color: #ffffff !important;
  border: none;
  border-radius: 5px;
  padding: 6px 10px;
  font-size: 14px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: none;
  margin-right: 12px;
  opacity: 1 !important;           /* asegurar opacidad plena */
  filter: none !important;
}

.modal-header .btn-close .material-icons {
  color: #ffffff !important;       /* blanco puro */
  font-size: 20px !important;      /* un poco más grande si hace falta */
  font-weight: 700 !important;
  line-height: 1 !important;
  text-shadow: none !important;    /* eliminar sombras que atenúan el blanco */
  opacity: 1 !important;
  display: inline-block;
  vertical-align: middle;
}

.modal-header .btn-close:hover {
  background-color: #7A1222 !important; /* variante oscura al hover */
}

/* body sin scroll en el modal; el scroll va dentro de logs-container */
.modal-body {
  padding: 16px;
  overflow: hidden !important; /* ocultar scroll visual del modal-body */
  -ms-overflow-style: none;    /* IE/Edge */
  scrollbar-width: none;       /* Firefox */
}
.modal-body::-webkit-scrollbar { /* Chrome, Safari, Opera */
  display: none;
}

/* la lista/tabla hará el scroll interno y no el modal entero */
.logs-container {
  max-height: calc(100vh - 160px); /* ajustar según header+footer */
  overflow-y: auto;
  margin-bottom: 0;        /* quitar margen inferior que complica el recorte */
  padding-bottom: 80px;    /* espacio interno para que el último elemento no quede tapado */
  box-sizing: border-box;  /* asegurar que padding se incluye en el cálculo */
}

/* espacio entre la caja de filtro y el resto */
.filter-container {
  margin: 18px 0 12px; /* arriba 18px, abajo 12px */
}

/* espacio a izquierda y derecha de la caja de fecha */
.filter-container input[type="date"],
.filter-container #filter-date {
  margin: 0 12px; /* 12px a izquierda y derecha */
  box-sizing: border-box;
}

/* separación entre la lista de logs y el footer (botones) */
.logs-container {
  margin-bottom: 16px;
}

/* pequeños ajustes verticales en el body para separar del borde del modal */
.modal-body {
  padding-top: 18px;
  padding-bottom: 18px;
}

/* Homogeneizar estilo (sin color) para los botones: borrar filtro, exportar y cerrar footer */
.logout-button,
.close-btn {
  border: none;
  border-radius: 5px;
  padding: 6px 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 0;
  width: auto;
  box-sizing: border-box;
  box-shadow: none;
  margin-right: 8px;
}

.close-btn {
  background-color: #99172A; /* rojo corporativo */
  color: #fff;
  border: none;
  border-radius: 5px;
  padding: 6px 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 0;
  width: auto;
  box-sizing: border-box;
  box-shadow: none;
  margin-right: 8px;
  margin-bottom: 12px;
}

.close-btn:hover {
  background-color: #7A1222; /* variante más oscura al pasar el ratón */
}

/* Exportar a Excel: usar mismo verde que "Agregar usuario" */
.btn-export {
  background-color: #2e7d32; /* mismo tono de verde */
  color: #fff;
}
.btn-export:hover {
  background-color: #1b5e20;
}

/* Forzar verde en el botón Exportar (tiene logout-button + btn-export) */
.logout-button.btn-export,
button.logout-button.btn-export {
  background-color: #2e7d32 !important;
  color: #fff !important;
}
.logout-button.btn-export:hover,
button.logout-button.btn-export:hover {
  background-color: #1b5e20 !important;
}

/* aplicar mismo tono gris de "Cerrar sesión" del header al botón "borrar filtro" */
.logout-button {
  background-color: #798a99; /* gris del header */
  color: #fff;
}
.logout-button:hover {
  background-color: #5e6e7a;
}

.modal-footer {
  padding: 12px 16px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-start; /* ahora los botones quedan a la izquierda */
  gap: 8px;
}

/* asegurar que el botón de cierre no tenga margen izquierdo que lo desplace */
.close-btn {
  margin-left: 0;
  margin-right: 8px;
}

/* backdrop semitransparente */
.modal-backdrop {
  position: fixed;
  inset: 0; /* top:0; right:0; bottom:0; left:0 */
  background-color: rgba(0, 0, 0, 0.7); /* opacidad 0.7 */
  z-index: 11990; /* justo por debajo del modal (.modal-content tiene z-index:12000) */
}

/* Formato de la tabla de logs */
.logs-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.95rem;
  color: #21343a;
  table-layout: auto;
}

/* Cabecera sticky para que siempre sea visible al hacer scroll */
.logs-table thead th {
  position: sticky;
  top: 0;
  background: linear-gradient(180deg,#fbfcfd,#f5f7f8);
  z-index: 4;
  padding: 10px 12px;
  text-align: left;
  font-weight: 600;
  border-bottom: 2px solid #e6eaee;
  color: #2b3b43;
}

/* Celdas */
.logs-table th,
.logs-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #eef2f4;
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Columnas con anchos fijos/semifijos */
.logs-table td:nth-child(1) { width: 60px; text-align: center; }
.logs-table td:nth-child(2) { width: 180px; max-width: 220px; }
.logs-table td:nth-child(3),
.logs-table td:nth-child(4) { width: 160px; text-align: center; }
.logs-table td.expulsado-column { width: 80px; text-align: center; }

/* La última columna (motivo de fallo) puede envolver texto */
.logs-table td:last-child {
  white-space: normal;
  max-width: 360px;
  word-wrap: break-word;
}

/* Filas zebra suave */
.logs-table tbody tr:nth-child(odd) {
  background: #ffffff;
}
.logs-table tbody tr:nth-child(even) {
  background: #fbfcfd;
}

/* Resaltar intentos fallidos y estilo del texto de fallo */
.login-fallido {
  background: rgba(217,83,79,0.04) !important;
}
.fallo {
  color: #d9534f;
  font-weight: 600;
}

/* Icono/check expulsado */
.expulsado-check {
  display: inline-block;
  background: #e6f7ea;
  color: #28a745;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 700;
  font-size: 0.95rem;
}

/* Hover de fila para mejor lectura */
.logs-table tbody tr:hover {
  background: #f1f6f8;
}

/* Responsive: en pantallas estrechas cambiar a layout compacto */
@media (max-width: 720px) {
  .logs-table th:nth-child(1),
  .logs-table td:nth-child(1) { display: none; } /* ocultar id para ahorrar espacio */
  .logs-table th,
  .logs-table td { padding: 8px 10px; font-size: 0.88rem; }
  .logs-table td:nth-child(2) { width: 28%; }
  .logs-table td:nth-child(3),
  .logs-table td:nth-child(4) { width: 22%; }
  .logs-table td:last-child { max-width: 200px; }
}

/* Asegurar que el contenedor .logs-container mantiene el scroll interno correcto */
.logs-container .logs-table {
  min-width: 720px; /* evita que columnas se colapsen en pantallas grandes con scroll horizontal */
}
</style>